import { posthog } from 'posthog-js'

import { AuthUser__UserFragment as User } from '@/graphql/api/generated'

function isPostHogActive(): boolean {
  return posthog.__loaded
}

/** Lowercased domain of an email address, or null when it is not a usable address. */
function emailDomain(email: string | null | undefined): string | null {
  if (!email) return null
  const at = email.lastIndexOf('@')
  if (at < 0) return null
  const domain = email
    .slice(at + 1)
    .trim()
    .toLowerCase()
  return domain || null
}

export const PostHogService = {
  identify(user: User): void {
    if (!isPostHogActive() || !user.id) return

    // Flag overrides are sticky on the client and identify() does not clear them, so the
    // domain set on the login screen survives every later identity change. That is how an
    // admin who impersonates a customer keeps evaluating flags as @communo.app and shows
    // them internal-only UI inside their own space. Whoever we have just identified is the
    // authority on this, so it is restated here rather than left to the last screen that
    // happened to set it.
    const domain = emailDomain(user.email)
    if (domain) {
      posthog.setPersonPropertiesForFlags({ email_domain: domain }, true)
    } else {
      // No domain to assert. Clearing this one key beats leaving the previous person's
      // behind — and only this key, since other overrides are not ours to drop.
      posthog.unsetPersonPropertiesForFlags(['email_domain'], true)
    }

    posthog.identify(user.id, {
      email: user.email,
      // Feature flags gate on the domain rather than the full address (see
      // setEmailDomainForFlags): keeping both in sync means one flag condition works for
      // identified users and for anonymous visitors on the login screen alike.
      email_domain: emailDomain(user.email),
      name: user.fullname,
      city: user.city,
      country: user.country,
      gender: user.gender,
      language: user.settings?.language,
      phone: user.phoneNumber?.nationalNumber,
      country_code: user.phoneNumber?.countryCode,
      avatar_url: user.avatar?.contentUrl,
      created_at: user.createdAt,
      circles: user.circleMemberships?.edges
        ?.map((edge) => edge.node!.circle.name)
        .join(', '),
      circles_count: user.circleMemberships?.edges?.length || 0,
    })

    posthog.setPersonProperties({
      email: user.email,
      email_domain: emailDomain(user.email),
      name: user.fullname,
      city: user.city,
      country: user.country,
      gender: user.gender,
      language: user.settings?.language,
    })
  },

  /**
   * Makes an email DOMAIN available to feature-flag evaluation without identifying anyone.
   *
   * This is what lets the login screen — which runs before authentication, with no distinct
   * person — evaluate a flag restricted to internal staff. Only the domain is sent, never the
   * address the visitor typed: the flag conditions only ever need the domain, and PostHog has
   * no business receiving a stranger's email.
   *
   * No-op when the domain cannot be read, so a partially typed address sends nothing.
   */
  setEmailDomainForFlags(email: string | null | undefined): void {
    if (!isPostHogActive()) return
    const domain = emailDomain(email)
    if (!domain) return
    posthog.setPersonPropertiesForFlags({ email_domain: domain }, true)
  },

  alias(distinctId: string): void {
    if (!isPostHogActive()) return
    posthog.alias(distinctId)
  },

  reset(): void {
    if (!isPostHogActive()) return
    posthog.reset()
  },

  capture(event: string, properties?: Record<string, any>): void {
    if (!isPostHogActive()) return
    posthog.capture(event, properties)
  },

  setPersonProperties(properties: Record<string, any>): void {
    if (!isPostHogActive()) return
    posthog.setPersonProperties(properties)
  },

  group(
    groupType: string,
    groupKey: string,

    properties?: Record<string, any>
  ): void {
    if (!isPostHogActive()) return
    posthog.group(groupType, groupKey, properties)
  },
}
